import { formatMinorUnits } from '@/api/money';
import {
  SEARCH_TYPES,
  type SearchGroup,
  type SearchHit,
  type SearchResults,
  type SearchType,
} from '@/api/search';
import type { ModuleId } from '@/modules/catalog';
import { StatusChip } from '@/ui/Badge';
import type { IconName } from '@/ui/Icon';
import { recordPath } from '@/ui/RecordLink';
import type {
  SearchComboboxEmpty,
  SearchComboboxGroup,
  SearchComboboxOption,
} from '@/ui/SearchCombobox';
import type { StatusChipKind } from '@/ui/statusLooks';
import { searchTypeLabel, searchTypeNoun } from '@/ui/vocabulary';
import { formatInZone } from '@/ui/zonedTime';
import {
  offeredPages,
  offeredPlaces,
  pageName,
  type PageRef,
  type PlaceRef,
  type ViewAccess,
} from './navigation';
import { newRequestPath } from './useNewRequest';
import type { RecentRecord } from './recentRecords';

/** How many pages one search offers; it is a jump target, not a site map. */
const MAX_PAGES = 6;

/** What choosing a row does: go somewhere, and remember the record if it was one. */
export interface SearchChoice {
  readonly to: string;
  readonly remember?: RecentRecord;
}

type Option = SearchComboboxOption<SearchChoice>;
type Group = SearchComboboxGroup<SearchChoice>;

/** Where each kind of record lives, and how a row for it is drawn. */
interface TypeLook {
  readonly module: ModuleId;
  readonly icon: IconName;
  /** The status vocabulary the record's `state` is worded in, for kinds that have one. */
  readonly status?: StatusChipKind;
  readonly open: (id: string) => string;
  /** The list page, for "See all". */
  readonly list: string;
}

const LOOKS: Record<SearchType, TypeLook> = {
  CUSTOMER: {
    module: 'CORE',
    icon: 'customers',
    open: (id) => recordPath('customer', id),
    list: '/customers',
  },
  JOB: {
    module: 'CORE',
    icon: 'jobs',
    status: 'job',
    open: (id) => recordPath('job', id),
    list: '/jobs',
  },
  DEAL: {
    module: 'CORE',
    icon: 'pipeline',
    status: 'pipeline',
    open: (id) => recordPath('deal', id),
    list: '/pipeline',
  },
  LEAD: {
    module: 'CORE',
    icon: 'leads',
    status: 'lead',
    open: (id) => recordPath('lead', id),
    list: '/leads',
  },
  PROPOSAL: {
    module: 'CORE',
    icon: 'proposals',
    status: 'proposal',
    open: (id) => recordPath('proposal', id),
    list: '/proposals',
  },
  INVOICE: {
    module: 'BILLING',
    icon: 'invoicing',
    status: 'invoice',
    open: (id) => recordPath('invoice', id),
    list: '/invoices',
  },
  MATERIAL: {
    module: 'INVENTORY',
    icon: 'inventory',
    open: (id) => `/inventory/${encodeURIComponent(id)}`,
    list: '/inventory',
  },
  SERVICE: {
    module: 'CORE',
    icon: 'pricebook',
    open: (id) => `/pricebook/services/${encodeURIComponent(id)}`,
    list: '/pricebook',
  },
  PERSON: {
    module: 'CORE',
    icon: 'user',
    open: (id) => `/team/${encodeURIComponent(id)}`,
    list: '/team',
  },
};

/**
 * The kinds of record this installation can open, so a search never asks for a hit that would
 * land on a module it does not ship.
 *
 * @param enabled whether a module is part of this release
 * @returns the kinds, in the order the groups are listed
 */
export function searchableTypes(enabled: (module: ModuleId) => boolean): readonly SearchType[] {
  return SEARCH_TYPES.filter((type) => enabled(LOOKS[type].module));
}

/** Whether what was typed names the page: its own name, its destination's, or an older name for it. */
function namesPage({ destination, view }: PageRef, words: string): boolean {
  return [view.label, destination.label, view.id, ...(view.keywords ?? [])].some((name) =>
    name.toLowerCase().includes(words),
  );
}

/** Whether what was typed names the part of a page: its own name or another word for it. */
function namesPlace({ place }: PlaceRef, words: string): boolean {
  return [place.label, ...(place.keywords ?? [])].some((name) =>
    name.toLowerCase().includes(words),
  );
}

/**
 * The pages, and the parts of them, whose name matches what was typed, listed first because they
 * need no wait.
 *
 * Every page this person may open is offered, not only the ones on their rail: the rail is shaped
 * by role, and whatever else they may open is one search away. A part of a page, such as the audit
 * trail in Settings, is offered by its own name and opens on its own.
 *
 * @param term what was typed, trimmed
 * @param access what the person holds and what this installation ships
 * @returns one group, or none when nothing matches
 */
export function pageGroups(term: string, access: ViewAccess): Group[] {
  const words = term.toLowerCase();
  const pages: Option[] = offeredPages(access)
    .filter((page) => namesPage(page, words))
    .map((page) => ({
      key: `page:${page.view.id}`,
      label: pageName(page),
      icon: page.destination.icon,
      data: { to: page.view.paths[0] },
    }));
  const places: Option[] = offeredPlaces(access)
    .filter((found) => namesPlace(found, words))
    .map(({ destination, place }) => ({
      key: `place:${place.to}`,
      label: `${place.label} · ${destination.label}`,
      icon: destination.icon,
      data: { to: place.to },
    }));
  const options = [...pages, ...places].slice(0, MAX_PAGES);
  return options.length === 0 ? [] : [{ key: 'pages', heading: 'Pages', options }];
}

function describeHit(hit: SearchHit, zone: string): string | null {
  const parts: string[] = [];
  if (hit.when !== null) {
    parts.push(formatInZone(hit.when, zone));
  }
  if (hit.amountMinor !== null && hit.currency !== null) {
    parts.push(formatMinorUnits(hit.amountMinor, hit.currency));
  }
  parts.push(...hit.details);
  return parts.length === 0 ? null : parts.join(' · ');
}

function sentenceCase(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}

function hitOption(hit: SearchHit, zone: string): Option {
  const look = LOOKS[hit.type];
  const detail = describeHit(hit, zone);
  const title = hit.title === '' ? sentenceCase(searchTypeNoun(hit.type)) : hit.title;
  return {
    key: `${hit.type}:${hit.id}`,
    label: title,
    ...(detail === null ? {} : { description: detail }),
    icon: look.icon,
    ...(look.status === undefined || hit.state === null
      ? {}
      : { trailing: <StatusChip kind={look.status} value={hit.state} /> }),
    data: { to: look.open(hit.id), remember: { type: hit.type, id: hit.id, title, detail } },
  };
}

function seeAllOption({ type }: SearchGroup, term: string): Option {
  const query = new URLSearchParams({ q: term });
  return {
    key: `all:${type}`,
    label: `See all ${searchTypeLabel(type).toLowerCase()}`,
    icon: 'chevron-right',
    kind: 'command',
    data: { to: `${LOOKS[type].list}?${query.toString()}` },
  };
}

/** Booking a job for the customer who is the top hit, offered where that customer is listed. */
function bookingGroups(group: SearchGroup, access: ViewAccess): Group[] {
  const [top] = group.items;
  const allowed = access.enabled('CORE') && access.can('JOB_READ') && access.can('JOB_WRITE');
  if (top === undefined || !allowed) {
    return [];
  }
  return [
    {
      key: 'quick',
      heading: 'Quick actions',
      options: [
        {
          key: 'book-job',
          label: `Book a job for ${top.title}`,
          icon: 'plus',
          kind: 'command',
          data: { to: `/jobs/new?${new URLSearchParams({ customer: top.id }).toString()}` },
        },
      ],
    },
  ];
}

export interface RecordContext {
  /** What was typed, trimmed. */
  readonly term: string;
  readonly access: ViewAccess;
  /** The workspace's time zone, which every time in a row is read in. */
  readonly zone: string;
}

/**
 * Turns what the server found into the rows to choose from.
 *
 * Each kind of record is a group headed by its name, with "See all" at the end of one that has
 * more than it shows. The client builds every path from the record's id; the server sends none.
 *
 * @param results what the search found
 * @param context what was typed, what the person may do, and the time zone to read times in
 * @returns the groups, in the order the server gave them
 */
export function recordGroups(
  results: SearchResults,
  { term, access, zone }: RecordContext,
): Group[] {
  return results.groups
    .filter((group) => access.enabled(LOOKS[group.type].module))
    .flatMap((group) => {
      const options = group.items.map((hit) => hitOption(hit, zone));
      if (group.hasMore) {
        options.push(seeAllOption(group, term));
      }
      const listed: Group = { key: group.type, heading: searchTypeLabel(group.type), options };
      return group.type === 'CUSTOMER' ? [listed, ...bookingGroups(group, access)] : [listed];
    });
}

/**
 * What to say, and offer, when nothing matched: the way on is to create the customer.
 *
 * @param term what was typed, trimmed
 * @param access what the person holds and what this installation ships
 * @returns the sentence, and the action when this person may create customers
 */
export function nothingFound(term: string, access: ViewAccess): SearchComboboxEmpty<SearchChoice> {
  const mayCreate =
    access.enabled('CORE') && access.can('CUSTOMER_READ') && access.can('CUSTOMER_WRITE');
  return {
    message: `No results for “${term}”`,
    actions: mayCreate
      ? [
          {
            key: 'create-customer',
            label: `Create customer “${term}”`,
            icon: 'plus',
            kind: 'command',
            data: { to: newRequestPath('/customers', term) },
          },
        ]
      : [],
  };
}

/**
 * The records opened lately, listed before anything is typed.
 *
 * @param recents what this browser remembers for the person
 * @param enabled whether a module is part of this release
 * @returns one group, or none when there is nothing to list
 */
export function recentGroups(
  recents: readonly RecentRecord[],
  enabled: (module: ModuleId) => boolean,
): Group[] {
  const options: Option[] = recents
    .filter((record) => enabled(LOOKS[record.type].module))
    .map((record) => ({
      key: `recent:${record.type}:${record.id}`,
      label: record.title,
      description: [sentenceCase(searchTypeNoun(record.type)), record.detail]
        .filter((part) => part !== null)
        .join(' · '),
      icon: LOOKS[record.type].icon,
      data: { to: LOOKS[record.type].open(record.id), remember: record },
    }));
  return options.length === 0 ? [] : [{ key: 'recent', heading: 'Recent', options }];
}
